import React, { useRef, useState } from 'react';
import useOnClickOutside from '../../../hooks/useOnClickOutside';
import { RiCloseLine, RiMenuLine } from 'react-icons/ri';
import ServiceTemplate from '../ServiceTemplate';
import { Link, matchPath, useLocation } from 'react-router-dom';
import { NavbarLinks } from '../../../data/navbarLinks';
import { BsChevronDown, BsChevronUp, BsMoonStars, BsSun } from 'react-icons/bs';
import { FaInstagram, FaLinkedin, FaGithub } from 'react-icons/fa';
import { contactPoints } from '../../../data/contactPoints';
import { useTheme } from '../../../ThemeContext';

const MobileNavbar = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef();
  const [accordionOpen, setAccordionOpen] = useState(false);
  const { isDark, toggleTheme } = useTheme();

  const matchRoute = (route) => matchPath({ path: route }, location.pathname);
  const handleClickOutside = () => setIsOpen(false);
  const handleMenuOpen = () => setIsOpen(true);
  const handleMenuClose = () => { setIsOpen(false); setAccordionOpen(false); };
  const handleAccordionToggle = () => setAccordionOpen((prev) => !prev);

  useOnClickOutside(menuRef, handleClickOutside);

  return (
    <div className="lg:hidden">
      {/* Hamburger */}
      {!isOpen && (
        <button
          onClick={handleMenuOpen}
          id="mobile-menu-open"
          className="flex items-center justify-center w-10 h-10 rounded-full transition-colors duration-300"
          style={{ color: 'var(--text-secondary)' }}
          aria-label="Open menu"
        >
          <RiMenuLine size={22} />
        </button>
      )}

      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-[9998] bg-black/30 backdrop-blur-sm transition-opacity duration-300
          ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        onClick={handleMenuClose}
      />

      {/* Slide-in Panel */}
      <div
        ref={menuRef}
        className={`fixed top-0 right-0 h-screen w-[85%] max-w-[380px] z-[9999]
                    glass shadow-2xl
                    transform transition-transform duration-500 ease-apple
                    ${isOpen ? 'translate-x-0' : 'translate-x-full'}
                    flex flex-col`}
        style={{ borderLeft: '1px solid var(--border-color)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5"
             style={{ borderBottom: '1px solid var(--border-color)' }}>
          <span className="text-lg font-bold tracking-tight" style={{ color: 'var(--text-primary)' }}>Menu</span>
          
          <div className="flex items-center gap-3">
             <button 
               onClick={toggleTheme}
               className="flex items-center justify-center w-9 h-9 rounded-full transition-all duration-500"
               style={{ background: 'var(--accent-soft)', color: 'var(--accent)' }}
               aria-label="Toggle Dark Mode"
             >
               {isDark ? <BsSun size={16} /> : <BsMoonStars size={14} />}
             </button>

             <button
               onClick={handleMenuClose}
               id="mobile-menu-close"
               className="flex items-center justify-center w-9 h-9 rounded-full transition-colors duration-300"
               style={{ background: 'var(--accent-soft)', color: 'var(--text-secondary)' }}
               aria-label="Close menu"
             >
               <RiCloseLine size={20} />
             </button>
          </div>
        </div>

        {/* Nav Links */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          <ul className="space-y-1">
            {NavbarLinks.map((link, index) => (
              <li key={index}>
                {link.title === 'Services' ? (
                  <div>
                    <button
                      onClick={handleAccordionToggle}
                      className="flex w-full items-center justify-between rounded-xl px-4 py-3.5
                                  text-base font-semibold transition-all duration-300"
                      style={{
                        color: matchRoute('/services/:serviceName') ? 'var(--accent)' : 'var(--text-primary)',
                        background: matchRoute('/services/:serviceName') ? 'var(--accent-soft)' : 'transparent'
                      }}
                    >
                      <span>{link.title}</span>
                      {accordionOpen
                        ? <BsChevronUp className="text-xs" style={{ color: 'var(--text-tertiary)' }} />
                        : <BsChevronDown className="text-xs" style={{ color: 'var(--text-tertiary)' }} />
                      }
                    </button>

                    <div
                      className={`overflow-hidden transition-all duration-400 ease-apple
                        ${accordionOpen ? 'max-h-[500px] opacity-100 mt-2' : 'max-h-0 opacity-0'}`}
                    >
                      <div className="rounded-xl p-4"
                           style={{ 
                             background: 'var(--bg-surface)', 
                             border: '1px solid var(--border-color)' 
                           }}>
                        <ServiceTemplate onLinkClick={handleMenuClose} />
                      </div>
                    </div>
                  </div>
                ) : (
                  <Link to={link.path} onClick={handleMenuClose}>
                    <span
                      className="block rounded-xl px-4 py-3.5 text-base font-semibold transition-all duration-300"
                      style={{
                        color: matchRoute(link?.path) ? 'var(--accent)' : 'var(--text-primary)',
                        background: matchRoute(link?.path) ? 'var(--accent-soft)' : 'transparent'
                      }}
                    >
                      {link.title}
                    </span>
                  </Link>
                )}
              </li>
            ))}
          </ul>

          {/* CTA */}
          <div className="mt-6">
            <Link
              to="/contactus"
              onClick={handleMenuClose}
              className="btn-nav-solid-blue w-full justify-center !py-2.5"
            >
              Get Started
            </Link>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-5" style={{ borderTop: '1px solid var(--border-color)' }}>
          <p className="text-xs font-medium mb-3" style={{ color: 'var(--text-tertiary)' }}>Follow Us</p>
          <div className="flex items-center gap-4">
            <a href={contactPoints.instagram} target="_blank" rel="noopener noreferrer"
               className="flex items-center justify-center w-10 h-10 rounded-full transition-all duration-300 hover:scale-110"
               style={{ background: 'var(--accent-soft)', color: 'var(--text-secondary)' }}>
              <FaInstagram size={18} />
            </a>
            <a href={contactPoints.linkedin} target="_blank" rel="noopener noreferrer"
               className="flex items-center justify-center w-10 h-10 rounded-full transition-all duration-300 hover:scale-110"
               style={{ background: 'var(--accent-soft)', color: 'var(--text-secondary)' }}>
              <FaLinkedin size={18} />
            </a>
            <a href={contactPoints.github} target="_blank" rel="noopener noreferrer"
               className="flex items-center justify-center w-10 h-10 rounded-full transition-all duration-300 hover:scale-110"
               style={{ background: 'var(--accent-soft)', color: 'var(--text-secondary)' }}>
               <FaGithub size={18} />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileNavbar;
